/**
 * Workspace scope: the switcher in the top bar narrows the console to a workspace's
 * projects. The store keeps the whole aggregate (`full`) and shows the scoped view; with no
 * scope the view IS the full data (same objects), so nothing changes for All projects.
 *
 * Scoped: projects, checkouts, sessions, pipelines, jobs (by their checkout's project) and
 * the feed (machine events with no project stay). Machines, suggestions, audit and machine
 * history are shared across workspaces and never scoped.
 */
import { pipelineKey } from '@orgus/protocol';
import type { FeedEvent, Id } from '@orgus/protocol';
import type { Entities } from './store.ts';

type Scoped = Pick<Entities, 'projects' | 'checkouts' | 'sessions' | 'pipelines' | 'jobs' | 'feed'>;

function filterRecord<T>(rec: Record<string, T>, keep: (x: T) => boolean): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(rec)) if (keep(v)) out[k] = v;
  return out;
}

/** Feed events in scope: machine-wide ones (no project) and the workspace's projects. */
export function feedInScope(e: Pick<FeedEvent, 'projectId'>, scope: ReadonlySet<Id> | null): boolean {
  return !scope || e.projectId == null || scope.has(e.projectId);
}

/**
 * A scoper remembers its last inputs, so a collection the latest patch did not touch keeps
 * its scoped identity too (selectors with useShallow then skip the re-render).
 */
export function createScoper() {
  let lastScope: ReadonlySet<Id> | null = null;
  let lastFull: Partial<Entities> = {};
  let lastOut: Partial<Scoped> = {};

  return function scope<E extends Entities>(full: E, scope: ReadonlySet<Id> | null): E {
    if (!scope) {
      lastScope = null;
      lastFull = {};
      lastOut = {};
      return full;
    }
    const sameScope = scope === lastScope;
    const pick = <K extends keyof Scoped>(k: K, make: () => Scoped[K], also: (keyof Scoped)[] = []): Scoped[K] => {
      const same = sameScope && [k, ...also].every((d) => lastFull[d] === full[d]);
      if (same && lastOut[k] !== undefined) return lastOut[k] as Scoped[K];
      const v = make();
      lastOut[k] = v;
      return v;
    };
    const projects = pick('projects', () => filterRecord(full.projects, (p) => scope.has(p.id)));
    const checkouts = pick('checkouts', () => filterRecord(full.checkouts, (c) => scope.has(c.projectId)));
    const sessions = pick('sessions', () => filterRecord(full.sessions, (s) => s.projectId != null && scope.has(s.projectId)));
    const pipelines = pick('pipelines', () => filterRecord(full.pipelines, (p) => scope.has(p.projectId)));
    const jobs = pick('jobs', () =>
      filterRecord(full.jobs, (j) => {
        const c = j.checkoutId ? full.checkouts[j.checkoutId] : undefined;
        return !!c && scope.has(c.projectId);
      }),
      ['checkouts'],
    );
    const feed = pick('feed', () => full.feed.filter((e) => feedInScope(e, scope)));
    lastScope = scope;
    lastFull = { projects: full.projects, checkouts: full.checkouts, sessions: full.sessions, pipelines: full.pipelines, jobs: full.jobs, feed: full.feed };
    return { ...full, projects, checkouts, sessions, pipelines, jobs, feed };
  };
}

/** What a deep link points at: a session, a checkout, a project or a pipeline (by its key). */
export type ScopedRef = { session: Id } | { checkout: Id } | { project: Id } | { pipeline: string };

/**
 * A deep link (a notification, a shared URL) can name something the chosen workspace hides.
 * Returns the project it belongs to (null: it has no project) when it exists but is outside
 * the scope, so the page can say so and offer All projects; null when it is in scope or
 * does not exist at all.
 */
export function outOfScope(
  full: Pick<Entities, 'sessions' | 'checkouts' | 'projects' | 'pipelines'>,
  scope: ReadonlySet<Id> | null,
  ref: ScopedRef,
): { projectId: Id | null } | null {
  if (!scope) return null;
  let projectId: Id | null | undefined;
  if ('session' in ref) projectId = full.sessions[ref.session] ? full.sessions[ref.session]!.projectId : undefined;
  else if ('checkout' in ref) projectId = full.checkouts[ref.checkout]?.projectId;
  else if ('project' in ref) projectId = full.projects[ref.project]?.id;
  else projectId = Object.values(full.pipelines).find((p) => pipelineKey(p) === ref.pipeline)?.projectId;
  if (projectId === undefined) return null;
  return projectId !== null && scope.has(projectId) ? null : { projectId };
}
